import { SpellingPreference } from '@heidi/schema/ml-scribe';
import { parseDateAsLocal } from '@heidi/utils/dates';
import { useQueryUser } from '@heidi/workspace/data';
import { format, isValid, parse } from 'date-fns';
import { useIntl } from 'react-intl';
import type { Country } from 'react-phone-number-input';

import { GENDER_OPTIONS } from '../../contracts';
import { useUserDateFormat } from '../hooks/useUserDateFormat';
import { capitalize, isFutureDob } from './identity';

const COUNTRY_NAME_TO_ISO: Record<string, Country> = {
  Australia: 'AU',
  'New Zealand': 'NZ',
  'United Kingdom': 'GB',
  'United States': 'US',
  Canada: 'CA',
  Japan: 'JP',
};

export interface IdentityEditorIntl {
  dobLabel: string;
  genderLabel: string;
  identifierLabel: string;
  dobPlaceholder: string;
  genderPlaceholder: string;
  identifierPlaceholder: string;
  phonePlaceholder: string;
  /** Default country for the phone-number input's flag selector. Falls back to
   *  AU if the user's `country` doesn't match a region we ship support for. */
  phoneDefaultCountry: Country;
  ageFormat: (age: number) => string;
  formatDob: (iso: string) => string;
  /** Normalises a user-typed date string back to ISO `yyyy-MM-dd`. Accepts
   *  both ISO format and the user's display format. Returns `null` if the
   *  input cannot be parsed so the caller can show an inline validation
   *  error instead of round-tripping garbage through the backend. Empty
   *  input returns `''` to signal "clear the field". */
  parseDob: (input: string) => string | null;
  /** Inline error message shown when a date of birth is in the future. */
  dobFutureMessage: string;
  /** Returns `dobFutureMessage` when the (already-parsed ISO) date is in the
   *  future, else `null`. Designed as a second-stage check after `parseDob`. */
  validateDobNotFuture: (iso: string) => string | null;
  genderOptions: Array<{ value: string; label: string }>;
  formatGender: (value: string) => string;
}

/** Shared intl + formatters for the identity-block inline editors, used by
 *  both the drawer and the full-page view. */
export const useIdentityEditorIntl = (): IdentityEditorIntl => {
  const intl = useIntl();
  const { data: userData } = useQueryUser((data) => ({
    session_settings: data?.session_settings,
    country: data?.country,
  }));
  const phoneDefaultCountry: Country =
    (userData?.country && COUNTRY_NAME_TO_ISO[userData.country]) || 'AU';
  // Same read the app spelled inline with `DEFAULT_DATE_FORMAT` from
  // `@/components/constants`: that barrel is app-side, and its value is the
  // schema enum member this hook already falls back to.
  const dateFormat = useUserDateFormat();

  const dobFutureMessage = intl.formatMessage({
    defaultMessage: 'Date of birth cannot be in the future',
    id: 't1NrC9HdS3',
  });

  const genderOptionLabels: Record<string, string> = {
    male: intl.formatMessage({ defaultMessage: 'Male', id: 'jIbAkyUeIY' }),
    female: intl.formatMessage({ defaultMessage: 'Female', id: '74BYXLpq8f' }),
    other: intl.formatMessage({ defaultMessage: 'Other', id: '/VnDMl81rh' }),
    unknown: intl.formatMessage({ defaultMessage: 'Unknown', id: '5jeq8PJZg7' }),
  };

  return {
    dobLabel: intl.formatMessage({ defaultMessage: 'Date of birth', id: 'GFTdXwO9W3' }),
    genderLabel: intl.formatMessage({ defaultMessage: 'Gender', id: 'm8/n8cq9i9' }),
    identifierLabel: intl.formatMessage({
      defaultMessage: 'Patient identifier',
      id: 'aWv7G4OvhI',
    }),
    dobPlaceholder: dateFormat.toLowerCase(),
    phonePlaceholder: (() => {
      // Numbers below are all libphonenumber-valid AND, where a regulator
      // publishes one, drawn from the official reserved-for-fiction range so a
      // placeholder can never resemble a real subscriber's number: US/CA use the
      // NANP 555-0100..0199 range, UK uses Ofcom's 07700 900xxx drama range, AU
      // uses ACMA's 0491 57x xxx range. NZ and Japan have no published reserved
      // mobile range, so they use realistic examples.
      // country is set at registration and is the most reliable regional signal
      switch (userData?.country) {
        case 'Australia':
          return 'e.g. 0491 570 156';
        case 'New Zealand':
          return 'e.g. 021 123 4567';
        case 'United Kingdom':
          return 'e.g. 07700 900123';
        case 'Canada':
          return 'e.g. (416) 555-0123';
        case 'United States':
          return 'e.g. (212) 555-0100';
        case 'Japan':
          return 'e.g. 090 1234 5678';
      }
      // fall back to spelling preference for accounts without a country set
      switch (userData?.session_settings?.spelling_preference) {
        case SpellingPreference.AU:
          return 'e.g. 0491 570 156';
        case SpellingPreference.UK:
          return 'e.g. 07700 900123';
        case SpellingPreference.CA:
          return 'e.g. (416) 555-0123';
        default:
          return 'e.g. (212) 555-0100';
      }
    })(),
    genderPlaceholder: intl.formatMessage({
      defaultMessage: 'Add gender',
      id: '9tAlT3A5zc',
    }),
    phoneDefaultCountry,
    identifierPlaceholder: intl.formatMessage({
      defaultMessage: 'e.g Jane Doe 27F',
      id: 'nSrztg+yEp',
    }),
    ageFormat: (age: number) =>
      intl.formatMessage({ defaultMessage: '{age} yrs', id: 'GFeWiJa6zD' }, { age }),
    formatDob: (iso: string): string => {
      const parsed = parseDateAsLocal(iso);
      return parsed ? format(parsed, dateFormat) : iso;
    },
    parseDob: (input: string): string | null => {
      if (input === '') return '';
      const fromIso = parseDateAsLocal(input);
      if (fromIso) return format(fromIso, 'yyyy-MM-dd');
      const fromDisplay = parse(input, dateFormat, new Date());
      if (isValid(fromDisplay)) return format(fromDisplay, 'yyyy-MM-dd');
      return null;
    },
    dobFutureMessage,
    validateDobNotFuture: (iso: string): string | null =>
      isFutureDob(iso) ? dobFutureMessage : null,
    genderOptions: GENDER_OPTIONS.map((o) => ({
      value: o.value,
      label: genderOptionLabels[o.value] ?? o.label,
    })),
    formatGender: (value: string) => genderOptionLabels[value] ?? capitalize(value),
  };
};
